<template>
  <fullscreen-dialog
    v-model="model"
    :title="title"
    :subtitle="`${firmReportItemInternal.menu}, ID ${firmReportItemInternal.company_id}`"
    hide-footer
  >
    <template #default>
      <v-card-text v-if="firmReportItemInternal">
        <v-row align="center">
          <v-col cols="auto">
            <month-picker
              v-model="date1"
              @updatedMonthDates="monthDates = $event"
              color="white"
            />
          </v-col>
          <v-col cols="auto">
            <text-field
              v-model="searchR"
              kind="search"
              :placeholder="$t('search')"
              hide-details
              color="white"
            ></text-field>
          </v-col>
          <v-checkbox
            :label="$t('show_all_days')"
            hide-details
            v-model="allDayFirm"
            dense
          >
          </v-checkbox>
          <v-checkbox
            class="ml-6"
            :label="$t('ordered_1')"
            hide-details
            v-model="ordered_meals"
            dense
          />
          <v-checkbox
            v-if="
              firmReportItemInternal.settings?.can_see_ordered ||
              $store.state.user.user?.type == 'restaurant'
            "
            :label="$t('taken')"
            hide-details
            v-model="taken_meals"
            dense
          />
          <v-radio-group v-model="type" row hide-details class="ml-6">
            <v-radio value="orders">
              <template v-slot:label>
                <span class="mb-2">{{ $t("orders") }}</span>
              </template>
            </v-radio>
            <v-radio value="foods">
              <template v-slot:label>
                <span class="mb-2">{{ $t("meals_main") }}</span>
              </template>
            </v-radio>
          </v-radio-group>
          <v-col align="end">
            <v-row no-gutters align="center" justify="end">
              <excel-button class="mr-2" @click="exportReportToExcel"></excel-button>
              <print-button @click="printReport"></print-button
            ></v-row>
          </v-col>
        </v-row>
        <v-data-table
          v-if="!loading"
          id="monthlyOrdersTable"
          :headers="dynamicTableHeaders"
          :items="processedTableData"
          :items-per-page="-1"
          class="ordera-box-shadow striped-table"
          :class="!device.isDesktop && 'mt-4'"
          hide-default-footer
          disable-pagination
          mobile-breakpoint="0"
          dense
        >
          <template v-slot:item.shift_one="{ item }">
            {{ formatShiftValue(item.shift_one) }}
          </template>
          <template v-slot:item.shift_two="{ item }">
            {{ formatShiftValue(item.shift_two) }}
          </template>
          <template v-slot:item.shift_three="{ item }">
            {{ formatShiftValue(item.shift_three) }}
          </template>
          <template v-slot:item.shift_four="{ item }">
            {{ formatShiftValue(item.shift_four) }}
          </template>
          <template v-slot:item.total="{ item }">
            <strong>{{ formatShiftValue(item.total) }}</strong>
          </template>
          <template v-slot:item.difference="{ item }">
            <strong>{{ item.difference }}</strong>
          </template>
          <template v-slot:body.append>
            <tr class="text-weight-bold">
              <td>{{ $t("total") }}</td>
              <td>{{ formatShiftValue(totals.shift_one) }}</td>
              <td>{{ formatShiftValue(totals.shift_two) }}</td>
              <td>{{ formatShiftValue(totals.shift_three) }}</td>
              <td>{{ formatShiftValue(totals.shift_four) }}</td>
              <td>
                <strong>{{ formatShiftValue(totals.total) }}</strong>
              </td>
              <td v-if="ordered_meals && taken_meals">
                <strong>{{ totals.total.quantity - totals.total.payed }}</strong>
              </td>
            </tr>
          </template>
        </v-data-table>
        <v-row justify="center" v-else>
          <v-progress-circular
            indeterminate
            color="primary"
            size="24"
          ></v-progress-circular>
        </v-row>
      </v-card-text>
    </template>
  </fullscreen-dialog>
</template>

<script>
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import hasModel from "@/mixins/hasModel";
import hasExcel from "@/mixins/hasExcel";
import hasPrinting from "@/mixins/hasPrinting";
import hasTakenReport from "../taken/mixins/hasTakenReport";

export default {
  components: {
    FullscreenDialog,
  },
  mixins: [hasModel, hasExcel, hasPrinting, hasTakenReport],
  name: "ReportMealsTakenCompanyMonthlyOrder",
  props: {
    firmReportItem: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      excelLoading: false,
      date1: new Date().toISOString().substr(0, 7), // YYYY-MM format
      monthDates: [],
      searchR: "",
      allDayFirm: false,
      taken_meals: false,
      ordered_meals: true,
      type: "orders",
      rawFirmDataByDate: {},
      firmReportItemInternal: {},
    };
  },
  methods: {
    formatShiftValue(shiftData) {
      if (this.ordered_meals && this.taken_meals) {
        return [shiftData.quantity, shiftData.payed].join("/");
      }
      if (this.ordered_meals) return shiftData.quantity;
      if (this.taken_meals) return shiftData.payed;
      return "";
    },
    formatRowForExport(item) {
      return {
        ...item,
        shift_one: this.formatShiftValue(item.shift_one),
        shift_two: this.formatShiftValue(item.shift_two),
        shift_three: this.formatShiftValue(item.shift_three),
        shift_four: this.formatShiftValue(item.shift_four),
        total: this.formatShiftValue(item.total),
        difference: this.ordered_meals && this.taken_meals ? item.difference : undefined,
      };
    },
    async getCompanyMonthlyOrders() {
      if (!this.firmReportItemInternal || !this.firmReportItemInternal.company_id) {
        this.rawFirmDataByDate = {};
        return;
      }
      this.loading = true;
      const year = this.year;
      const month = this.month;
      const companyId = this.firmReportItemInternal.company_id;

      // Get first and last day of the month
      const firstDay = `${year}-${String(month).padStart(2, "0")}-01`;
      const lastDayDate = new Date(year, month, 0);
      const lastDay = `${year}-${String(month).padStart(2, "0")}-${String(
        lastDayDate.getDate()
      ).padStart(2, "0")}`;
      await this.fetchTakenReport(
        {
          date: firstDay,
          date2: lastDay,
        },
        companyId
      );
      this.loading = false;
    },
    async exportReportToExcel() {
      // Sanitize worksheet name to remove forbidden characters: * ? : \ / [ ]
      const sanitizeSheetName = (name) =>
        name
          .replace(/[*?:\\/\[\]]/g, " ")
          .trim()
          .substring(0, 31);

      this.downloadExcel({
        fileName: this.exportTitle + ".xlsx",
        sheets: [
          {
            name: sanitizeSheetName(this.exportTitle),
            headers: this.dynamicTableHeaders,
            items: this.exportItems,
            title: this.exportTitle,
            subtitle: this.monthName,
          },
        ],
      });
    },
    printReport() {
      this.printDocument({
        title: this.exportTitle,
        subtitle: this.monthName,
        tables: [
          {
            headers: this.dynamicTableHeaders,
            items: this.exportItems,
          },
        ],
      });
    },
  },
  computed: {
    title() {
      if (this.ordered_meals) {
        return this.taken_meals
          ? this.$t("ordered_taken_foods")
          : this.$t("ordered_foods");
      }
      return this.$t("taken_foods");
    },
    exportTitle() {
      let title = this.firmReportItemInternal.menu;
      return title + " " + this.title.toLowerCase();
    },
    month() {
      return parseInt(this.date1.split("-")[1]);
    },
    year() {
      return parseInt(this.date1.split("-")[0]);
    },
    monthName() {
      return this.localeDate(this.date1, { month: "long", year: "numeric" });
    },
    dynamicTableHeaders() {
      const headers = [
        {
          text: this.$t("day"),
          value: "day",
          sortable: false,
        },
        { text: this.$t("1st_shift"), value: "shift_one" },
        { text: this.$t("2nd_shift"), value: "shift_two" },
        { text: this.$t("3rd_shift"), value: "shift_three" },
        { text: this.$t("4th_shift"), value: "shift_four" },
        { text: this.$t("total"), value: "total" },
      ];
      if (this.ordered_meals && this.taken_meals) {
        headers.push({ text: this.$t("difference"), value: "difference" });
      }
      return headers;
    },
    exportItems() {
      const totalsRow = {
        day: this.$t("total"),
        shift_one: this.totals.shift_one,
        shift_two: this.totals.shift_two,
        shift_three: this.totals.shift_three,
        shift_four: this.totals.shift_four,
        total: this.totals.total,
        difference: this.totals.total
          ? this.totals.total.quantity - this.totals.total.payed
          : 0,
      };
      return [
        ...this.processedTableData.map((item) => this.formatRowForExport(item)),
        this.formatRowForExport(totalsRow),
      ];
    },
    processedTableData() {
      const orderIds = new Set(this.reportItem.map((item) => item.id));
      const singleOrders =
        this.type === "foods"
          ? this.reportItem
          : [...orderIds].map((id) => this.reportItem.find((item) => item.id === id));
      const dates = this.allDayFirm
        ? this.monthDates
        : new Set(singleOrders.map((item) => item.date));

      return [...dates].map((date) => {
        const dateOrders = singleOrders.filter((item) => item.date === date);

        const calculateShiftOrders = (shift) => {
          const shiftOrders = dateOrders.filter((item) => item.shift === shift);
          return {
            quantity: shiftOrders.reduce((sum, item) => sum + item.quantity, 0),
            payed: shiftOrders.reduce((sum, item) => sum + item.quantity_payed, 0),
          };
        };

        const totalQuantity = dateOrders.reduce((sum, item) => sum + item.quantity, 0);
        const totalPayed = dateOrders.reduce((sum, item) => sum + item.quantity_payed, 0);

        return {
          day: this.localeDate(date, { day: "2-digit", month: "short", year: "numeric" }),
          shift_one: calculateShiftOrders(1),
          shift_two: calculateShiftOrders(2),
          shift_three: calculateShiftOrders(3),
          shift_four: calculateShiftOrders(4),
          total: {
            quantity: totalQuantity,
            payed: totalPayed,
          },
          difference: totalQuantity - totalPayed,
        };
      });
    },
    totals() {
      const tableData = this.processedTableData;
      return {
        shift_one: {
          quantity: tableData.reduce((sum, item) => sum + item.shift_one.quantity, 0),
          payed: tableData.reduce((sum, item) => sum + item.shift_one.payed, 0),
        },
        shift_two: {
          quantity: tableData.reduce((sum, item) => sum + item.shift_two.quantity, 0),
          payed: tableData.reduce((sum, item) => sum + item.shift_two.payed, 0),
        },
        shift_three: {
          quantity: tableData.reduce((sum, item) => sum + item.shift_three.quantity, 0),
          payed: tableData.reduce((sum, item) => sum + item.shift_three.payed, 0),
        },
        shift_four: {
          quantity: tableData.reduce((sum, item) => sum + item.shift_four.quantity, 0),
          payed: tableData.reduce((sum, item) => sum + item.shift_four.payed, 0),
        },
        total: {
          quantity: tableData.reduce((sum, item) => sum + item.total.quantity, 0),
          payed: tableData.reduce((sum, item) => sum + item.total.payed, 0),
        },
      };
    },
  },
  watch: {
    model: {
      handler(newVal) {
        if (newVal) {
          // firmReportItemInternal is now primarily managed by firmReportItem watcher
          this.getCompanyMonthlyOrders();
        } else {
          this.rawFirmDataByDate = {};
          this.searchR = "";
          // Reset other relevant states if necessary
        }
      },
    },
    firmReportItem: {
      handler(newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          this.firmReportItemInternal = structuredClone(newVal);
        } else {
          this.firmReportItemInternal = {};
        }
        if (this.model) {
          // If dialog is open, refresh data
          this.getCompanyMonthlyOrders();
        }
      },
      deep: true,
      immediate: true, // Initialize firmReportItemInternal on component load
    },
    date1: {
      handler() {
        if (this.model) {
          // Only fetch if dialog is open
          this.getCompanyMonthlyOrders();
        }
      },
    },
  },
};
</script>
